import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Rocket, Clock, CheckCircle, Hourglass, Layers, GripVertical, Plus, MoreHorizontal,
    Trash2, Wand2, ChevronLeft, ChevronRight, User, X, ChevronsUpDown, UserPlus, Eye,
    Mail, Megaphone, Rss, Video, FileText, Lightbulb, Search, Bot, Users, Calendar, ShieldCheck,
    AlertTriangle
} from 'lucide-react';
import apiService from '../../services/apiService';

// --- Reusable UI Components & Configuration ---
const statusOrder = ['Backlog', 'Pending Approval', 'Scheduled', 'In-Progress', 'Review', 'Complete'];
const statusConfig = {
    'Backlog': { icon: <Hourglass size={16} />, color: 'bg-slate-600', textColor: 'text-slate-300' },
    'Pending Approval': { icon: <Rocket size={16} />, color: 'bg-yellow-600', textColor: 'text-yellow-300' },
    'Scheduled': { icon: <Clock size={16} />, color: 'bg-purple-600', textColor: 'text-purple-300' },
    'In-Progress': { icon: <Hourglass size={16} />, color: 'bg-brand-600', textColor: 'text-brand-300' },
    'Review': { icon: <CheckCircle size={16} />, color: 'bg-orange-600', textColor: 'text-orange-300' },
    'Complete': { icon: <CheckCircle size={16} />, color: 'bg-green-600', textColor: 'text-green-300' },
};

const taskTypeConfig = {
    'email': { icon: <Mail size={18} />, name: 'Email' },
    'webinar': { icon: <Video size={18} />, name: 'Webinar' },
    'blog': { icon: <Rss size={18} />, name: 'Blog Post' },
    'social': { icon: <Megaphone size={18} />, name: 'Social Post' },
    'ad': { icon: <FileText size={18} />, name: 'Advertisement' },
    'seo': { icon: <Search size={18} />, name: 'SEO Audit' },
    'idea': { icon: <Lightbulb size={18} />, name: 'Idea' },
    'default': { icon: <Layers size={18} />, name: 'Task' }
};

const originConfig = {
    'Campaign': 'bg-sky-500/20 text-sky-300',
    'SEO Audit': 'bg-teal-500/20 text-teal-300',
    'Idea Hangar': 'bg-amber-500/20 text-amber-300',
    'Manual Entry': 'bg-slate-600/50 text-slate-300',
};

const useClickOutside = (ref, callback) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [ref, callback]);
};

const Card = ({ children, className = '' }) => ( <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md ${className}`}>{children}</div> );

// --- THIS IS THE FIX: Full implementation of CampaignsWithCalendarView ---
const CampaignsWithCalendarView = ({ tasks, campaigns, onViewTask }) => {
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());

    const changeMonth = (amount) => { setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1)); };

    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const filteredTasks = useMemo(() => tasks.filter(task => {
        if (!task.date) return false;
        const taskDate = new Date(task.date);
        return taskDate.getFullYear() === year && taskDate.getMonth() === month;
    }), [tasks, year, month]);

    const campaignMap = useMemo(() => new Map(campaigns.map(c => [c._id, c])), [campaigns]);

    const getDynamicCampaignStatus = (campaign) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(campaign.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + (campaign.duration * 7));

        if (endDate < today) return { text: 'Complete', color: 'green' };
        if (startDate > today) return { text: 'Scheduled', color: 'purple' };
        if (startDate <= today && endDate >= today && campaign.status === 'Active') {
            return { text: 'In-Progress', color: 'brand' };
        }
        return { text: campaign.status, color: 'slate' };
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6 animate-fade-in">
            <div className="flex-grow">
                <Card className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={() => changeMonth(-1)} className="p-2 rounded-md hover:bg-slate-700"><ChevronLeft /></button>
                        <h2 className="text-xl font-bold text-white">{currentDate.toLocaleString('default', { month: 'long' })} {year}</h2>
                        <button onClick={() => changeMonth(1)} className="p-2 rounded-md hover:bg-slate-700"><ChevronRight /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-400 mb-2">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day}>{day}</div>)}</div>
                    <div className="grid grid-cols-7 gap-1">
                        {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} className="border border-slate-900 rounded-md"></div>)}
                        {Array.from({ length: daysInMonth }).map((_, day) => {
                            const dayNumber = day + 1;
                            const tasksForDay = filteredTasks.filter(t => new Date(t.date).getUTCDate() === dayNumber);
                            return (
                                <div key={day} className="h-28 p-1 border border-slate-700/50 bg-slate-800/20 rounded-md flex flex-col overflow-hidden">
                                    <span className="font-semibold text-slate-300">{dayNumber}</span>
                                    <div className="space-y-1 mt-1 overflow-y-auto pr-1">
                                        {tasksForDay.map(task => {
                                            const campaign = campaignMap.get(task.campaignId);
                                            const color = campaign ? (getDynamicCampaignStatus(campaign).color) : 'gray';
                                            return (
                                                <button key={task._id} onClick={() => onViewTask(task)} className={`w-full text-left p-1 rounded text-xs text-white bg-${color}-600/30 border-l-2 border-${color}-400`}>
                                                    {task.title}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>
            <div className="w-full lg:w-80 lg:flex-shrink-0">
                <Card className="p-4">
                    <h3 className="text-lg font-bold text-white mb-4">Campaigns</h3>
                    <div className="space-y-3">
                        {campaigns.map(camp => {
                            const dynamicStatus = getDynamicCampaignStatus(camp);
                            return (
                                <button key={camp._id} onClick={() => setSelectedCampaignId(camp._id)} className={`w-full p-3 rounded-lg text-left transition-colors bg-slate-700/50 hover:bg-slate-700`}>
                                    <h4 className="font-bold text-white">{camp.name}</h4>
                                    <p className={`text-sm font-semibold text-${dynamicStatus.color}-400`}>{dynamicStatus.text}</p>
                                </button>
                            )
                        })}
                    </div>
                </Card>
            </div>
        </div>
    );
};

// --- THIS IS THE FIX: Full implementation of AddTaskModal ---
const AddTaskModal = ({ isOpen, onClose, onSave }) => {
    const [title, setTitle] = useState('');
    if (!isOpen) return null;

    const handleSave = () => {
        if (title.trim()) {
            onSave(title);
            setTitle('');
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
            <Card className="w-full max-w-md p-6">
                <h2 className="text-lg font-bold text-white mb-4">Add New Task</h2>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter task title..."
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white mb-4"
                />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 bg-slate-600 rounded-lg text-sm">Cancel</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-brand-500 rounded-lg text-sm">Save Task</button>
                </div>
            </Card>
        </div>
    );
};

// --- THIS IS THE FIX: Full implementation of TaskDetailModal ---
const TaskDetailModal = ({ task, onClose, onUpdate, teamMembers }) => {
    const modalRef = useRef();
    useClickOutside(modalRef, onClose);
    if (!task) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 animate-fade-in-fast p-4">
            <div ref={modalRef} className="w-full max-w-4xl bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
                <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">{task.title}</h2>
                    <button onClick={onClose} className="p-1 text-slate-500 hover:text-white rounded-full hover:bg-slate-700"><X size={20} /></button>
                </div>
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    <p className="text-sm text-slate-400 mb-6">From: <span className="font-semibold text-brand-400">{task.origin}</span></p>
                    <div className="mb-6">
                        <h3 className="text-md font-semibold text-slate-200 mb-2">Details & Description</h3>
                        <div className="text-slate-300 bg-slate-900/50 p-4 rounded-md text-sm whitespace-pre-wrap min-h-[100px]">
                            {task.description || 'No description provided.'}
                        </div>
                    </div>
                </div>
                 <div className="bg-slate-900/50 px-6 py-4 flex justify-end rounded-b-lg">
                    <button onClick={onClose} className="px-4 py-2 rounded-md font-semibold text-sm bg-brand-500 text-white hover:bg-brand-400 transition-colors">Close</button>
                </div>
            </div>
        </div>
    );
};

// --- THIS IS THE FIX: Full implementation of ConfirmationModal ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
            <Card className="w-full max-w-md p-6">
                <div className="text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                    <h2 className="text-lg font-bold text-white mt-4">{title}</h2>
                    <p className="mt-2 text-sm text-slate-400">{message}</p>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-slate-600 rounded-lg text-sm">Cancel</button>
                    <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Confirm</button>
                </div>
            </Card>
        </div>
    );
};


const TasksListView = ({ 
    tasks, onUpdate, onDelete, onView, teamMembers, onAddTask, onBulkUpdate, onBulkDelete,
    selectedTasks, handleSelectOne, handleSelectAll, onClearSelection,
    originFilter, setOriginFilter, uniqueOrigins,
    typeFilter, setTypeFilter, uniqueTypes
}) => {
    
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <Card className="p-4">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-white">All Missions</h2>
                    <select value={originFilter} onChange={e => setOriginFilter(e.target.value)} className="bg-slate-700 text-sm rounded-md p-1.5 border border-slate-600">
                        {uniqueOrigins.map(o => <option key={o} value={o}>{o === 'all' ? 'All Origins' : o}</option>)}
                    </select>
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="bg-slate-700 text-sm rounded-md p-1.5 border border-slate-600">
                        {uniqueTypes.map(t => <option key={t} value={t}>{t === 'all' ? 'All Types' : taskTypeConfig[t]?.name || t}</option>)}
                    </select>
                </div>
                <button onClick={onAddTask} className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-semibold hover:bg-brand-400 transition-colors">
                    <Plus size={16} /> Add Task
                </button>
            </div>

            {selectedTasks.size > 0 && (
                <div className="bg-slate-700/50 p-2 rounded-md mb-4 flex items-center gap-4 text-sm">
                    <span className="font-semibold">{selectedTasks.size} selected</span>
                    <select onChange={e => onBulkUpdate({ status: e.target.value })} className="bg-slate-600 rounded p-1">
                        <option>Change Status</option>
                        {statusOrder.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select onChange={e => onBulkUpdate({ assignee: e.target.value })} className="bg-slate-600 rounded p-1">
                        <option>Assign to...</option>
                        <option value="AI">AI</option>
                        {teamMembers.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                    </select>
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={onBulkDelete} className="flex items-center gap-1 px-2 py-1 bg-red-600/80 text-white rounded-md text-xs hover:bg-red-600">
                            <Trash2 size={14} /> Delete
                        </button>
                        <button onClick={onClearSelection} title="Clear Selection" className="flex items-center gap-1 px-2 py-1 bg-slate-600/80 text-white rounded-md text-xs hover:bg-slate-600">
                            <X size={14} /> Clear
                        </button>
                    </div>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-300">
                    <thead className="text-xs text-slate-400 uppercase bg-slate-700/50">
                        <tr>
                            <th scope="col" className="p-4"><input type="checkbox" onChange={handleSelectAll} className="bg-slate-600" /></th>
                            <th scope="col" className="px-4 py-3">Type</th>
                            <th scope="col" className="px-4 py-3">Task Title</th>
                            <th scope="col" className="px-4 py-3">Origin</th>
                            <th scope="col" className="px-4 py-3">Status</th>
                            <th scope="col" className="px-4 py-3">Assignee</th>
                            <th scope="col" className="px-4 py-3">Scheduled</th>
                            <th scope="col" className="px-4 py-3">Due By</th>
                            <th scope="col" className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.map(task => {
                            const originKey = (task.origin || '').split(':')[0].trim();
                            return (
                                <tr key={task._id} className="border-b border-slate-700 hover:bg-slate-800/50">
                                    <td className="p-4"><input type="checkbox" checked={selectedTasks.has(task._id)} onChange={() => handleSelectOne(task._id)} className="bg-slate-600" /></td>
                                    <td className="px-4 py-2" title={taskTypeConfig[task.taskType]?.name}>{taskTypeConfig[task.taskType]?.icon || taskTypeConfig.default.icon}</td>
                                    <td className="px-4 py-2 font-medium text-white">{task.title}</td>
                                    <td className="px-4 py-2"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${originConfig[originKey] || originConfig['Manual Entry']}`}>{originKey}</span></td>
                                    <td className="px-4 py-2">
                                        <select value={task.status} onChange={(e) => onUpdate(task._id, { status: e.target.value })} className="bg-transparent border-0 text-xs p-0 focus:ring-0">
                                            {statusOrder.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-4 py-2">
                                         <select value={task.assignee || ''} onChange={(e) => onUpdate(task._id, { assignee: e.target.value })} className="bg-transparent border-0 text-xs p-0 focus:ring-0">
                                            <option value="">Unassigned</option>
                                            <option value="AI">AI</option>
                                            {teamMembers.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-4 py-2">{formatDate(task.date)}</td>
                                    <td className="px-4 py-2">{formatDate(task.dueDate)}</td>
                                    <td className="px-4 py-2 text-right flex justify-end gap-2">
                                        <button onClick={() => onView(task)} className="p-2 text-slate-400 hover:text-brand-400"><Eye size={16} /></button>
                                        <button onClick={() => onDelete(task._id)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

// --- Main Mission Control View Component ---
export default function MissionControlView() {
    const [activeTab, setActiveTab] = useState('tasks');
    const [selectedTask, setSelectedTask] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [originFilter, setOriginFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [tasksData, campaignData, teamData] = await Promise.all([
                apiService.fetchFlightDeckTasks(),
                apiService.fetchCampaigns(),
                apiService.fetchTeamMembers()
            ]);
            setTasks(tasksData || []);
            setCampaigns(campaignData || []);
            setTeamMembers(teamData || []);
        } catch (error) {
            console.error("Failed to fetch Mission Control data:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllData();
        const handleFocus = () => fetchAllData();
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [fetchAllData]);

    const allMissionItems = useMemo(() => {
        const enrichedTasks = tasks.map(task => {
            if (!task.date) {
                return { ...task, dueDate: null, taskType: task.taskType || 'default' };
            }
            const dueDate = new Date(task.date);
            dueDate.setDate(dueDate.getDate() - 1);
            
            let taskType = 'default';
            if (task.origin?.startsWith('Campaign')) {
                const title = task.title.toLowerCase();
                if (title.includes('email')) taskType = 'email';
                else if (title.includes('webinar')) taskType = 'webinar';
                else if (title.includes('blog')) taskType = 'blog';
                else if (title.includes('post')) taskType = 'social';
                else if (title.includes('ad')) taskType = 'ad';
            }
            
            return {
                ...task,
                dueDate: dueDate.toISOString(),
                taskType: task.taskType || taskType,
            };
        });

        const calendarCampaignActions = campaigns.flatMap(campaign =>
            (campaign.timeline || []).filter(item => item.scheduledDate).map(item => ({
                _id: `${campaign._id}-${item.id}`,
                title: item.action,
                date: new Date(item.scheduledDate).toISOString(),
                status: 'Scheduled',
                campaignId: campaign._id,
                description: item.details?.content || `Action from campaign '${campaign.name}'`,
                origin: `Campaign: ${campaign.name}`,
            }))
        );
        
        return {
            forListView: enrichedTasks,
            // --- THIS IS THE FIX: Corrected the typo from 'enrich' to 'enrichedTasks' ---
            forCalendarView: [...enrichedTasks, ...calendarCampaignActions]
        };

    }, [tasks, campaigns]);

    const uniqueOrigins = useMemo(() => ['all', ...new Set(allMissionItems.forListView.map(t => (t.origin || '').split(':')[0].trim()))], [allMissionItems.forListView]);
    const uniqueTypes = useMemo(() => ['all', ...new Set(allMissionItems.forListView.map(t => t.taskType))], [allMissionItems.forListView]);

    const filteredTasks = useMemo(() => {
        return allMissionItems.forListView.filter(task => {
            const originMatch = originFilter === 'all' || (task.origin || '').startsWith(originFilter);
            const typeMatch = typeFilter === 'all' || task.taskType === typeFilter;
            return originMatch && typeMatch;
        });
    }, [allMissionItems.forListView, originFilter, typeFilter]);
    
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedTasks(new Set(filteredTasks.map(t => t._id)));
        } else {
            setSelectedTasks(new Set());
        }
    };

    const handleSelectOne = (taskId) => {
        setSelectedTasks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) newSet.delete(taskId);
            else newSet.add(taskId);
            return newSet;
        });
    };
    const handleBulkUpdate = async (updateData) => {
        const taskIds = Array.from(selectedTasks);
        if (taskIds.length === 0) return;
        try {
            await Promise.all(taskIds.map(id => apiService.updateFlightDeckTask(id, updateData)));
        } catch (error) {
            console.error("Failed to perform bulk update:", error);
        } finally {
            setSelectedTasks(new Set());
            await fetchAllData();
        }
    };

    const handleUpdateTask = async (taskId, updateData) => {
        try {
            await apiService.updateFlightDeckTask(taskId, updateData);
            await fetchAllData();
        } catch (error) {
            console.error("Failed to update task:", error);
        }
    };
    
    const handleSaveNewTask = async (title) => {
        try {
            await apiService.createFlightDeckTask({ title, status: 'Backlog', origin: 'Manual Entry' });
            await fetchAllData();
        } catch (error) {
            console.error("Failed to create task:", error);
        }
    };
    
    const handleViewTask = (task) => setSelectedTask(task);
    const handleCloseModal = () => setSelectedTask(null);

    const handleDeleteTask = (taskId) => {
        setConfirmation({
            isOpen: true,
            title: 'Delete Task',
            message: 'Are you sure you want to permanently delete this task?',
            onConfirm: async () => {
                setConfirmation({ isOpen: false });
                try {
                    await apiService.deleteFlightDeckTask(taskId);
                    await fetchAllData();
                } catch (error) {
                    console.error("Failed to delete task:", error);
                }
            },
        });
    };
    
    const handleBulkDelete = () => {
        const taskIds = Array.from(selectedTasks);
        if (taskIds.length === 0) return;
        setConfirmation({
            isOpen: true,
            title: `Delete ${taskIds.length} Tasks`,
            message: 'Are you sure you want to permanently delete all selected tasks?',
            onConfirm: async () => {
                setConfirmation({ isOpen: false });
                try {
                    await Promise.all(taskIds.map(id => apiService.deleteFlightDeckTask(id)));
                } catch (error) {
                    console.error("Failed to perform bulk delete:", error);
                } finally {
                    setSelectedTasks(new Set());
                    await fetchAllData();
                }
            },
        });
    };

    const handleClearSelection = () => {
        setSelectedTasks(new Set());
    };

    return (
        <div className="animate-fade-in">
            <TaskDetailModal task={selectedTask} onClose={handleCloseModal} onUpdate={handleUpdateTask} teamMembers={teamMembers} />
            <AddTaskModal isOpen={isAddTaskModalOpen} onClose={() => setIsAddTaskModalOpen(false)} onSave={handleSaveNewTask} />
            <ConfirmationModal {...confirmation} onClose={() => setConfirmation({ isOpen: false })} />
            
            <div className="flex border-b border-slate-700 mb-6">
                <button onClick={() => setActiveTab('tasks')} className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'tasks' ? 'text-brand-400 border-b-2 border-brand-400' : 'text-slate-400 hover:text-brand-400/70'}`}>All Missions (Tasks)</button>
                <button onClick={() => setActiveTab('campaigns')} className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'campaigns' ? 'text-brand-400 border-b-2 border-brand-400' : 'text-slate-400 hover:text-brand-400/70'}`}>Campaigns & Calendar</button>
            </div>

            {isLoading ? <p className="text-slate-400">Loading missions...</p> : (
                 activeTab === 'tasks' ? 
                 <TasksListView 
                     tasks={filteredTasks}
                     onUpdate={handleUpdateTask}
                     onDelete={handleDeleteTask}
                     onView={handleViewTask}
                     teamMembers={teamMembers}
                     onAddTask={() => setIsAddTaskModalOpen(true)}
                     onBulkUpdate={handleBulkUpdate}
                     onBulkDelete={handleBulkDelete}
                     selectedTasks={selectedTasks}
                     handleSelectOne={handleSelectOne}
                     handleSelectAll={handleSelectAll}
                     onClearSelection={handleClearSelection}
                     originFilter={originFilter}
                     setOriginFilter={setOriginFilter}
                     uniqueOrigins={uniqueOrigins}
                     typeFilter={typeFilter}
                     setTypeFilter={setTypeFilter}
                     uniqueTypes={uniqueTypes}
                 /> : 
                 <CampaignsWithCalendarView 
                     tasks={allMissionItems.forCalendarView}
                     campaigns={campaigns}
                     onViewTask={handleViewTask}
                 />
            )}
        </div>
    );
}